import React, { useEffect, useMemo, useRef, useState } from "react";
import { useApp } from "../../contexts/AppContext";
import { apiService } from "../../services/api";
import { Category, Shop, RouteEntity, Event as MallEvent } from "../../types";
import styles from "./LowerSection.module.css";
import tabContentStyles from "../TabContent/TabContent.module.css";
import { useLocation } from "react-router-dom";
import RouteQRModal from "../RouteQRModal/RouteQRModal";

interface LowerSectionProps {
  activeTabId?: string | null;
}

const LowerSection: React.FC<LowerSectionProps> = ({
  activeTabId: activeTabIdProp,
}) => {
  const {
    state,
    setSelectedShop,
    setCurrentFloor,
    setActiveRoutePoints,
    setActiveTabId,
    setSelectedShopReload,
    setTerminalName,
    setCurrentFloorReload,
    setSelectedEvent,
  } = useApp();
  const activeTabId = activeTabIdProp ?? state.activeTabId;
  const shop = state.selectedShop;
  const routerLocation = useLocation();

  // Map tab id is 1
  const isMapTab = useMemo(() => {
    const currentTab = state.tabs?.find((t) => t.id === activeTabId);
    return currentTab ? parseInt(currentTab.id) === 1 : false;
  }, [activeTabId, state.tabs]);

  // Check if current tab is editable
  const isEditableTab = useMemo(() => {
    const currentTab = state.tabs?.find((t) => t.id === activeTabId);
    return currentTab ? currentTab.isEditable === true : false;
  }, [activeTabId, state.tabs]);

  // Detect if current tab is the Events tab by name
  const isEventsTab = useMemo(() => {
    const currentTab = state.tabs?.find((t) => t.id === activeTabId);
    if (!currentTab) return false;
    const nameRu = currentTab.name?.ru?.toLowerCase?.() || "";
    const nameEn = currentTab.name?.en?.toLowerCase?.() || "";
    return nameRu.includes("событ") || nameEn.includes("event");
  }, [activeTabId, state.tabs]);

  const [floorShops, setFloorShops] = useState<Shop[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const [relatedShops, setRelatedShops] = useState<Shop[]>([]);
  const [events, setEvents] = useState<MallEvent[]>([]);
  const shopsGridSimilarRef = useRef<HTMLDivElement>(null);
  const eventsGridRef = useRef<HTMLDivElement>(null);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);

  // Handle "More details" button click
  const handleMoreDetails = () => {
    if (!shop) return;

    // Get tabId from shop or from its category
    let shopTabId: number | undefined = shop.tabId;

    if (!shopTabId) {
      // Try to get tabId from categories
      const shopCategories =
        shop.categories || (shop.category ? [shop.category] : []);
      if (shopCategories.length > 0 && shopCategories[0].tabId) {
        shopTabId = shopCategories[0].tabId;
      }
    }

    if (!shopTabId) {
      console.warn("Cannot determine tabId for shop:", shop.id);
      return;
    }

    // Set selected shop and switch to the tab
    setSelectedShop(shop);
    setActiveTabId(String(shopTabId));
  };

  // Load shops for current floor if on map tab and no shop selected
  useEffect(() => {
    if (!isMapTab || shop) {
      setFloorShops([]);
      setSelectedCategoryId(null);
      return;
    }
    let mounted = true;
    apiService
      .getShops({ floor: state.currentFloor })
      .then((shops) => {
        if (!mounted) return;
        setFloorShops(shops || []);
      })
      .catch(() => {
        if (!mounted) return;
        setFloorShops([]);
      }) as unknown as void;
    return () => {
      mounted = false;
    };
  }, [isMapTab, shop, state.currentFloor]);

  // Load shops with same categories as selected shop for editable tabs
  useEffect(() => {
    if (!isEditableTab) {
      setRelatedShops([]);
      return;
    }

    if (!shop) {
      setRelatedShops([]);
      return;
    }

    // Get categories from shop (support both old and new format)
    const shopCategories =
      shop.categories || (shop.category ? [shop.category] : []);
    if (shopCategories.length === 0) {
      setRelatedShops([]);
      return;
    }

    let mounted = true;
    const categoryIds = shopCategories.map((cat) => cat.id);

    // Load all shops (not filtered by tab) to find shops with matching categories
    apiService
      .getShops({})
      .then((allShops) => {
        if (!mounted) return;
        // Filter shops that have at least one category in common with selected shop
        // Exclude the selected shop itself
        const filtered = allShops.filter((s) => {
          if (s.id === shop.id) return false; // Exclude selected shop
          const sCategories = s.categories || (s.category ? [s.category] : []);
          const sCategoryIds = sCategories.map((cat) => cat.id);
          // Check if there's any common category
          return categoryIds.some((catId) => sCategoryIds.includes(catId));
        });

        // Log to console
        const selectedShopName =
          state.language === "en"
            ? shop.nameEn && shop.nameEn.trim() !== ""
              ? shop.nameEn
              : shop.nameRu
            : shop.nameRu;
        const categoryNames = shopCategories
          .map((cat) =>
            state.language === "en"
              ? cat.nameEn && cat.nameEn.trim() !== ""
                ? cat.nameEn
                : cat.nameRu
              : cat.nameRu,
          )
          .join(", ");
        const relatedShopNames = filtered.map((s) =>
          state.language === "en"
            ? s.nameEn && s.nameEn.trim() !== ""
              ? s.nameEn
              : s.nameRu
            : s.nameRu,
        );

        console.log("=== Объекты с теми же категориями ===");
        console.log("Выбранный объект:", selectedShopName);
        console.log("Категории выбранного объекта:", categoryNames);
        console.log("Список объектов с теми же категориями:", relatedShopNames);
        console.log("Количество найденных объектов:", relatedShopNames.length);

        setRelatedShops(filtered || []);
      })
      .catch(() => {
        if (!mounted) return;
        setRelatedShops([]);
      });
    return () => {
      mounted = false;
    };
  }, [isEditableTab, shop]);

  // Derive unique categories from shops
  const uniqueCategories: Category[] = useMemo(() => {
    const map = new Map<string, Category>();
    for (const s of floorShops) {
      const cats = (
        s.categories && s.categories.length
          ? s.categories
          : s.category
            ? [s.category]
            : []
      ) as Category[];
      for (const c of cats) {
        if (c && !map.has(c.id)) {
          map.set(c.id, c);
        }
      }
    }
    return Array.from(map.values());
  }, [floorShops]);

  // Initialize selected category when categories list changes
  useEffect(() => {
    if (uniqueCategories.length === 0) {
      setSelectedCategoryId(null);
      return;
    }
    if (
      !selectedCategoryId ||
      !uniqueCategories.find((c) => c.id === selectedCategoryId)
    ) {
      setSelectedCategoryId(uniqueCategories[0].id);
    }
  }, [uniqueCategories, selectedCategoryId]);

  // Filter shops by selected category
  const filteredShops: Shop[] = useMemo(() => {
    if (!selectedCategoryId) return [];
    return floorShops.filter((s) => {
      const cats = (
        s.categories && s.categories.length
          ? s.categories
          : s.category
            ? [s.category]
            : []
      ) as Category[];
      return cats.some((c) => c?.id === selectedCategoryId);
    });
  }, [floorShops, selectedCategoryId]);

  // Clear events when switching to events tab
  useEffect(() => {
    if (isEventsTab) {
      setEvents([]);
    }
  }, [activeTabId, isEventsTab]);

  // Load events when on events tab and an event is selected
  useEffect(() => {
    if (!isEventsTab || !state.selectedEvent) {
      if (!isEventsTab) {
        setEvents([]);
      }
      return;
    }

    let mounted = true;
    apiService
      .getEvents({ active: true })
      .then((allEvents) => {
        if (!mounted) return;
        setEvents(allEvents || []);
      })
      .catch(() => {
        if (!mounted) return;
        setEvents([]);
      });
    return () => {
      mounted = false;
    };
  }, [isEventsTab, state.selectedEvent, state.language]);

  // Handle horizontal scroll with mouse wheel for shopsGridSimilar
  useEffect(() => {
    const container = shopsGridSimilarRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      // Only handle wheel events if the container can scroll horizontally
      if (container.scrollWidth > container.clientWidth) {
        e.preventDefault();
        container.scrollLeft += e.deltaY;
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [relatedShops]);

  // Handle horizontal scroll with mouse wheel for events grid
  useEffect(() => {
    const container = eventsGridRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      // Only handle wheel events if the container can scroll horizontally
      if (container.scrollWidth > container.clientWidth) {
        e.preventDefault();
        container.scrollLeft += e.deltaY;
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [events]);

  // For editable tabs, show shops with same categories if shop is selected
  // This check must come BEFORE the general shop check to have priority
  if (isEditableTab) {
    if (shop) {
      // Show shops with same categories
      return (
        <section
          className={styles.container}
          aria-label={
            state.language === "en" ? "Related shops" : "Похожие объекты"
          }
        >
          <div>
            <div className={styles.headerTitleSimilar}>
              {state.language === "en" ? "Related shops" : "Похожие объекты"}
            </div>
          </div>
          <div className={styles.shopsGridSimilar} ref={shopsGridSimilarRef}>
            {relatedShops.map((s) => {
              const title =
                state.language === "en"
                  ? s.nameEn && s.nameEn.trim() !== ""
                    ? s.nameEn
                    : s.nameRu
                  : s.nameRu;
              const workingHours =
                state.language === "en"
                  ? s.workingHoursEn && s.workingHoursEn.trim() !== ""
                    ? s.workingHoursEn
                    : s.workingHoursRu
                  : s.workingHoursRu;
              const floor =
                state.language === "en"
                  ? `${s.floor} floor `
                  : `${s.floor} этаж`;
              return (
                <div
                  key={s.id}
                  className={styles.shopCardSimilar}
                  onClick={() => setSelectedShop(s)}
                >
                  {s.logoUrl ? (
                    <img
                      className={styles.shopLogoSimilar}
                      src={`${process.env.REACT_APP_MEDIA_PATH}${s.logoUrl}`}
                      alt={title}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "data:image/svg+xml,%3Csvg width='348' height='210' viewBox='0 0 348 210' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='348' height='210' fill='white'/%3E%3Cpath d='M120.405 46.0949C120.056 45.7462 119.642 45.4696 119.187 45.2809C118.731 45.0922 118.243 44.9951 117.75 44.9951C117.257 44.9951 116.769 45.0922 116.313 45.2809C115.857 45.4696 115.444 45.7462 115.095 46.0949C114.746 46.4435 114.47 46.8574 114.281 47.313C114.092 47.7685 113.995 48.2568 113.995 48.7499C113.995 49.2429 114.092 49.7312 114.281 50.1867C114.47 50.6423 114.746 51.0562 115.095 51.4049L125.662 61.9649C122.946 65.7678 121.49 70.3265 121.5 74.9999V135C121.5 140.967 123.87 146.69 128.09 150.91C132.31 155.129 138.032 157.5 144 157.5H204C208.673 157.509 213.232 156.054 217.035 153.337L227.595 163.905C228.299 164.609 229.254 165.005 230.25 165.005C231.246 165.005 232.201 164.609 232.905 163.905C233.609 163.201 234.005 162.246 234.005 161.25C234.005 160.254 233.609 159.299 232.905 158.595L120.405 46.0949ZM169.605 105.907C168.306 106.459 167.123 107.251 166.117 108.24L131.092 142.65C129.721 140.334 128.998 137.691 129 135V74.9999C129 72.2249 129.757 69.6149 131.077 67.3799L169.605 105.907ZM144 150C141.225 150 138.637 149.25 136.41 147.945L171.375 113.595C172.076 112.908 173.018 112.523 174 112.523C174.981 112.523 175.924 112.908 176.625 113.595L211.59 147.945C209.288 149.293 206.668 150.002 204 150H144ZM219 74.9999V134.092L225.735 140.827C226.245 138.962 226.5 137.02 226.5 135V74.9999C226.5 69.0325 224.129 63.3095 219.91 59.09C215.69 54.8704 209.967 52.4999 204 52.4999H144C141.975 52.4999 140.032 52.7549 138.172 53.2649L144.9 59.9999H204C207.978 59.9999 211.793 61.5802 214.606 64.3933C217.42 67.2063 219 71.0216 219 74.9999ZM204 86.2499C204 84.7725 203.709 83.3096 203.144 81.9447C202.578 80.5798 201.749 79.3396 200.705 78.2949C199.66 77.2502 198.42 76.4216 197.055 75.8562C195.69 75.2908 194.227 74.9999 192.75 74.9999C191.272 74.9999 189.81 75.2908 188.445 75.8562C187.08 76.4216 185.84 77.2502 184.795 78.2949C183.75 79.3396 182.922 80.5798 182.356 81.9447C181.791 83.3096 181.5 84.7725 181.5 86.2499C181.5 89.2335 182.685 92.095 184.795 94.2048C186.905 96.3146 189.766 97.4999 192.75 97.4999C195.734 97.4999 198.595 96.3146 200.705 94.2048C202.815 92.095 204 89.2335 204 86.2499ZM196.5 86.2499C196.5 87.2444 196.105 88.1982 195.402 88.9015C194.698 89.6048 193.744 89.9999 192.75 89.9999C191.755 89.9999 190.801 89.6048 190.098 88.9015C189.395 88.1982 189 87.2444 189 86.2499C189 85.2553 189.395 84.3015 190.098 83.5982C190.801 82.8949 191.755 82.4999 192.75 82.4999C193.744 82.4999 194.698 82.8949 195.402 83.5982C196.105 84.3015 196.5 85.2553 196.5 86.2499Z' fill='%23019648'/%3E%3C/svg%3E";
                      }}
                    />
                  ) : (
                    <img
                      className={styles.shopLogoSimilar}
                      src="data:image/svg+xml,%3Csvg width='348' height='210' viewBox='0 0 348 210' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='348' height='210' fill='white'/%3E%3Cpath d='M120.405 46.0949C120.056 45.7462 119.642 45.4696 119.187 45.2809C118.731 45.0922 118.243 44.9951 117.75 44.9951C117.257 44.9951 116.769 45.0922 116.313 45.2809C115.857 45.4696 115.444 45.7462 115.095 46.0949C114.746 46.4435 114.47 46.8574 114.281 47.313C114.092 47.7685 113.995 48.2568 113.995 48.7499C113.995 49.2429 114.092 49.7312 114.281 50.1867C114.47 50.6423 114.746 51.0562 115.095 51.4049L125.662 61.9649C122.946 65.7678 121.49 70.3265 121.5 74.9999V135C121.5 140.967 123.87 146.69 128.09 150.91C132.31 155.129 138.032 157.5 144 157.5H204C208.673 157.509 213.232 156.054 217.035 153.337L227.595 163.905C228.299 164.609 229.254 165.005 230.25 165.005C231.246 165.005 232.201 164.609 232.905 163.905C233.609 163.201 234.005 162.246 234.005 161.25C234.005 160.254 233.609 159.299 232.905 158.595L120.405 46.0949ZM169.605 105.907C168.306 106.459 167.123 107.251 166.117 108.24L131.092 142.65C129.721 140.334 128.998 137.691 129 135V74.9999C129 72.2249 129.757 69.6149 131.077 67.3799L169.605 105.907ZM144 150C141.225 150 138.637 149.25 136.41 147.945L171.375 113.595C172.076 112.908 173.018 112.523 174 112.523C174.981 112.523 175.924 112.908 176.625 113.595L211.59 147.945C209.288 149.293 206.668 150.002 204 150H144ZM219 74.9999V134.092L225.735 140.827C226.245 138.962 226.5 137.02 226.5 135V74.9999C226.5 69.0325 224.129 63.3095 219.91 59.09C215.69 54.8704 209.967 52.4999 204 52.4999H144C141.975 52.4999 140.032 52.7549 138.172 53.2649L144.9 59.9999H204C207.978 59.9999 211.793 61.5802 214.606 64.3933C217.42 67.2063 219 71.0216 219 74.9999ZM204 86.2499C204 84.7725 203.709 83.3096 203.144 81.9447C202.578 80.5798 201.749 79.3396 200.705 78.2949C199.66 77.2502 198.42 76.4216 197.055 75.8562C195.69 75.2908 194.227 74.9999 192.75 74.9999C191.272 74.9999 189.81 75.2908 188.445 75.8562C187.08 76.4216 185.84 77.2502 184.795 78.2949C183.75 79.3396 182.922 80.5798 182.356 81.9447C181.791 83.3096 181.5 84.7725 181.5 86.2499C181.5 89.2335 182.685 92.095 184.795 94.2048C186.905 96.3146 189.766 97.4999 192.75 97.4999C195.734 97.4999 198.595 96.3146 200.705 94.2048C202.815 92.095 204 89.2335 204 86.2499ZM196.5 86.2499C196.5 87.2444 196.105 88.1982 195.402 88.9015C194.698 89.6048 193.744 89.9999 192.75 89.9999C191.755 89.9999 190.801 89.6048 190.098 88.9015C189.395 88.1982 189 87.2444 189 86.2499C189 85.2553 189.395 84.3015 190.098 83.5982C190.801 82.8949 191.755 82.4999 192.75 82.4999C193.744 82.4999 194.698 82.8949 195.402 83.5982C196.105 84.3015 196.5 85.2553 196.5 86.2499Z' fill='%23019648'/%3E%3C/svg%3E"
                      alt={title}
                    />
                  )}
                  <div className={styles.shopWorkingFloorSimilar}>{floor}</div>
                  <div className={styles.shopWorkingHoursSimilar}>
                    {workingHours}
                  </div>
                </div>
              );
            })}
            {relatedShops.length === 0 && (
              <div className={styles.emptyState}>
                {state.language === "en"
                  ? "No related shops found"
                  : "Похожие обьекты не найдены"}
              </div>
            )}
          </div>
        </section>
      );
    } else {
      // Show empty block with background
      return (
        <section
          className={styles.container}
          aria-label={state.language === "en" ? "Empty" : "Пусто"}
        >
          {/* Empty block with background #333 is already applied via .container */}
        </section>
      );
    }
  }

  // When on map tab: if shop selected, show shop info; otherwise show categories and filtered shops
  if (isMapTab) {
    // If shop is selected on map, show shop information
    if (shop) {
      const name =
        state.language === "en"
          ? shop.nameEn && shop.nameEn.trim() !== ""
            ? shop.nameEn
            : shop.nameRu
          : shop.nameRu;
      const shortDescription =
        state.language === "en"
          ? shop.shortDescriptionEn && shop.shortDescriptionEn.trim() !== ""
            ? shop.shortDescriptionEn
            : shop.shortDescriptionRu
          : shop.shortDescriptionRu;
      const location =
        state.language === "en"
          ? shop.locationEn && shop.locationEn.trim() !== ""
            ? shop.locationEn
            : shop.locationRu
          : shop.locationRu;
      const workingHours =
        state.language === "en"
          ? shop.workingHoursEn && shop.workingHoursEn.trim() !== ""
            ? shop.workingHoursEn
            : shop.workingHoursRu
          : shop.workingHoursRu;

      return (
        // выбранный объект в карте

        <section
          className={styles.container}
          aria-label={
            state.language === "en" ? "Selected object" : "Выбранный объект"
          }
        >
          <div>
            <div className={styles.metaRow}>
              <span className={styles.meta}>
                {shop.floor} {state.language === "en" ? "floor" : "этаж"}
              </span>
              {location && <span className={styles.metaLoc}>{location}</span>}
            </div>
          </div>
          <div className={styles.content}>
            <div className={styles.cardContainerMap}>
              <div>
                <img
                  className={styles.logo}
                  src={
                    shop.logoUrl
                      ? `${process.env.REACT_APP_MEDIA_PATH}${shop.logoUrl}`
                      : "data:image/svg+xml,%3Csvg width='348' height='210' viewBox='0 0 348 210' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='348' height='210' fill='white'/%3E%3Cpath d='M120.405 46.0949C120.056 45.7462 119.642 45.4696 119.187 45.2809C118.731 45.0922 118.243 44.9951 117.75 44.9951C117.257 44.9951 116.769 45.0922 116.313 45.2809C115.857 45.4696 115.444 45.7462 115.095 46.0949C114.746 46.4435 114.47 46.8574 114.281 47.313C114.092 47.7685 113.995 48.2568 113.995 48.7499C113.995 49.2429 114.092 49.7312 114.281 50.1867C114.47 50.6423 114.746 51.0562 115.095 51.4049L125.662 61.9649C122.946 65.7678 121.49 70.3265 121.5 74.9999V135C121.5 140.967 123.87 146.69 128.09 150.91C132.31 155.129 138.032 157.5 144 157.5H204C208.673 157.509 213.232 156.054 217.035 153.337L227.595 163.905C228.299 164.609 229.254 165.005 230.25 165.005C231.246 165.005 232.201 164.609 232.905 163.905C233.609 163.201 234.005 162.246 234.005 161.25C234.005 160.254 233.609 159.299 232.905 158.595L120.405 46.0949ZM169.605 105.907C168.306 106.459 167.123 107.251 166.117 108.24L131.092 142.65C129.721 140.334 128.998 137.691 129 135V74.9999C129 72.2249 129.757 69.6149 131.077 67.3799L169.605 105.907ZM144 150C141.225 150 138.637 149.25 136.41 147.945L171.375 113.595C172.076 112.908 173.018 112.523 174 112.523C174.981 112.523 175.924 112.908 176.625 113.595L211.59 147.945C209.288 149.293 206.668 150.002 204 150H144ZM219 74.9999V134.092L225.735 140.827C226.245 138.962 226.5 137.02 226.5 135V74.9999C226.5 69.0325 224.129 63.3095 219.91 59.09C215.69 54.8704 209.967 52.4999 204 52.4999H144C141.975 52.4999 140.032 52.7549 138.172 53.2649L144.9 59.9999H204C207.978 59.9999 211.793 61.5802 214.606 64.3933C217.42 67.2063 219 71.0216 219 74.9999ZM204 86.2499C204 84.7725 203.709 83.3096 203.144 81.9447C202.578 80.5798 201.749 79.3396 200.705 78.2949C199.66 77.2502 198.42 76.4216 197.055 75.8562C195.69 75.2908 194.227 74.9999 192.75 74.9999C191.272 74.9999 189.81 75.2908 188.445 75.8562C187.08 76.4216 185.84 77.2502 184.795 78.2949C183.75 79.3396 182.922 80.5798 182.356 81.9447C181.791 83.3096 181.5 84.7725 181.5 86.2499C181.5 89.2335 182.685 92.095 184.795 94.2048C186.905 96.3146 189.766 97.4999 192.75 97.4999C195.734 97.4999 198.595 96.3146 200.705 94.2048C202.815 92.095 204 89.2335 204 86.2499ZM196.5 86.2499C196.5 87.2444 196.105 88.1982 195.402 88.9015C194.698 89.6048 193.744 89.9999 192.75 89.9999C191.755 89.9999 190.801 89.6048 190.098 88.9015C189.395 88.1982 189 87.2444 189 86.2499C189 85.2553 189.395 84.3015 190.098 83.5982C190.801 82.8949 191.755 82.4999 192.75 82.4999C193.744 82.4999 194.698 82.8949 195.402 83.5982C196.105 84.3015 196.5 85.2553 196.5 86.2499Z' fill='url(%23paint0_linear_5310_528)'/%3E%3Cdefs%3E%3ClinearGradient id='paint0_linear_5310_528' x1='174' y1='44.9951' x2='174' y2='165.005' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%2364A696'/%3E%3Cstop offset='0.5' stop-color='%2364A696'/%3E%3Cstop offset='1' stop-color='%2364A696'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E"
                  }
                  alt={name}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "data:image/svg+xml,%3Csvg width='348' height='210' viewBox='0 0 348 210' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='348' height='210' fill='white'/%3E%3Cpath d='M120.405 46.0949C120.056 45.7462 119.642 45.4696 119.187 45.2809C118.731 45.0922 118.243 44.9951 117.75 44.9951C117.257 44.9951 116.769 45.0922 116.313 45.2809C115.857 45.4696 115.444 45.7462 115.095 46.0949C114.746 46.4435 114.47 46.8574 114.281 47.313C114.092 47.7685 113.995 48.2568 113.995 48.7499C113.995 49.2429 114.092 49.7312 114.281 50.1867C114.47 50.6423 114.746 51.0562 115.095 51.4049L125.662 61.9649C122.946 65.7678 121.49 70.3265 121.5 74.9999V135C121.5 140.967 123.87 146.69 128.09 150.91C132.31 155.129 138.032 157.5 144 157.5H204C208.673 157.509 213.232 156.054 217.035 153.337L227.595 163.905C228.299 164.609 229.254 165.005 230.25 165.005C231.246 165.005 232.201 164.609 232.905 163.905C233.609 163.201 234.005 162.246 234.005 161.25C234.005 160.254 233.609 159.299 232.905 158.595L120.405 46.0949ZM169.605 105.907C168.306 106.459 167.123 107.251 166.117 108.24L131.092 142.65C129.721 140.334 128.998 137.691 129 135V74.9999C129 72.2249 129.757 69.6149 131.077 67.3799L169.605 105.907ZM144 150C141.225 150 138.637 149.25 136.41 147.945L171.375 113.595C172.076 112.908 173.018 112.523 174 112.523C174.981 112.523 175.924 112.908 176.625 113.595L211.59 147.945C209.288 149.293 206.668 150.002 204 150H144ZM219 74.9999V134.092L225.735 140.827C226.245 138.962 226.5 137.02 226.5 135V74.9999C226.5 69.0325 224.129 63.3095 219.91 59.09C215.69 54.8704 209.967 52.4999 204 52.4999H144C141.975 52.4999 140.032 52.7549 138.172 53.2649L144.9 59.9999H204C207.978 59.9999 211.793 61.5802 214.606 64.3933C217.42 67.2063 219 71.0216 219 74.9999ZM204 86.2499C204 84.7725 203.709 83.3096 203.144 81.9447C202.578 80.5798 201.749 79.3396 200.705 78.2949C199.66 77.2502 198.42 76.4216 197.055 75.8562C195.69 75.2908 194.227 74.9999 192.75 74.9999C191.272 74.9999 189.81 75.2908 188.445 75.8562C187.08 76.4216 185.84 77.2502 184.795 78.2949C183.75 79.3396 182.922 80.5798 182.356 81.9447C181.791 83.3096 181.5 84.7725 181.5 86.2499C181.5 89.2335 182.685 92.095 184.795 94.2048C186.905 96.3146 189.766 97.4999 192.75 97.4999C195.734 97.4999 198.595 96.3146 200.705 94.2048C202.815 92.095 204 89.2335 204 86.2499ZM196.5 86.2499C196.5 87.2444 196.105 88.1982 195.402 88.9015C194.698 89.6048 193.744 89.9999 192.75 89.9999C191.755 89.9999 190.801 89.6048 190.098 88.9015C189.395 88.1982 189 87.2444 189 86.2499C189 85.2553 189.395 84.3015 190.098 83.5982C190.801 82.8949 191.755 82.4999 192.75 82.4999C193.744 82.4999 194.698 82.8949 195.402 83.5982C196.105 84.3015 196.5 85.2553 196.5 86.2499Z' fill='url(%23paint0_linear_5310_528)'/%3E%3Cdefs%3E%3ClinearGradient id='paint0_linear_5310_528' x1='174' y1='44.9951' x2='174' y2='165.005' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%2364A696'/%3E%3Cstop offset='0.5' stop-color='%2364A696'/%3E%3Cstop offset='1' stop-color='%2364A696'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E";
                  }}
                />
              </div>
              <div>
                {workingHours && (
                  <div className={styles.hours}>{workingHours}</div>
                )}
              </div>
            </div>
            <div className={styles.nameInfoContainer}>
              <div className={styles.name}>{name}</div>

              {shortDescription && (
                <div className={styles.short}>{shortDescription}</div>
              )}
            </div>
            <div className={styles.actions}>
              <button
                className={`${styles.button} ${styles.primary}`}
                onClick={async () => {
                  if (!shop) return;
                  const params = new URLSearchParams(routerLocation.search);
                  const terminalName = params.get("terminalName");
                  try {
                    // Resolve terminal by name
                    const terminals = await apiService.getTerminals({
                      active: true,
                    });
                    const terminal = terminalName
                      ? terminals.find((t) => t.name === terminalName)
                      : undefined;
                    if (!terminal) {
                      setActiveRoutePoints(undefined);
                      return;
                    }
                    // Find route for terminal+shop
                    const routes: RouteEntity[] = await apiService.getRoutes();
                    const match = routes.find(
                      (r) =>
                        String(r.shopId) === String(shop.id) &&
                        String(r.terminalId) === String(terminal.id),
                    );
                    if (match && match.points && match.points.length > 0) {
                      setActiveRoutePoints(match.points);
                      // Save route data for reload
                      setSelectedShopReload(shop);
                      setTerminalName(terminalName || undefined);
                      setCurrentFloorReload(terminal.floor);
                      console.log("Route saved for reload (LowerSection):", {
                        shop: shop.id,
                        terminalName,
                        floor: terminal.floor,
                      });
                      // Open the floor where the terminal is located
                      if (state.currentFloor !== terminal.floor) {
                        setCurrentFloor(terminal.floor);
                      }
                    } else {
                      setActiveRoutePoints(undefined);
                    }
                  } catch (e) {
                    setActiveRoutePoints(undefined);
                  }
                }}
              >
                {state.language === "en" ? "Build Route" : "ПОСТРОИТЬ МАРШРУТ"}
              </button>
              {/* {state.activeRoutePoints && state.activeRoutePoints.length > 0 && (
                <button
                  className={styles.button}
                  onClick={() => {
                    const params = new URLSearchParams(routerLocation.search);
                    const terminalName = params.get("terminalName") || state.terminalName || null;
                    setIsQRModalOpen(true);
                  }}
                >
                  {state.language === "en" ? "Route with you" : "МАРШРУТ С СОБОЙ"}
                </button>
              )} */}
              <button className={styles.button} onClick={handleMoreDetails}>
                {state.language === "en" ? "More details" : "ПОДРОБНЕЕ"}
              </button>
            </div>
          </div>
        </section>
      );
    }

    // If no shop selected on map, show categories and filtered shops
    return (
      <section
        className={styles.container}
        aria-label={state.language === "en" ? "Categories" : "Категории"}
      >
        {/* <div className={styles.headerTitle}>
          {state.language === "en" ? "CATEGORIES" : "КАТЕГОРИИ"}
        </div> */}
        {uniqueCategories.length > 0 ? (
          <div className={styles.categoriesBar}>
            {uniqueCategories.map((c, index) => {
              const title =
                state.language === "en"
                  ? c.nameEn && c.nameEn.trim() !== ""
                    ? c.nameEn
                    : c.nameRu
                  : c.nameRu;
              const isActive = c.id === selectedCategoryId;
              return (
                <React.Fragment key={c.id}>
                  <button
                    className={`${styles.categoryChip} ${
                      isActive ? styles.categoryChipActive : ""
                    }`}
                    onClick={() => setSelectedCategoryId(c.id)}
                  >
                    {title}
                  </button>
                  {index < uniqueCategories.length - 1 && (
                    <div className={styles.categoryDivider} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        ) : (
          <div className={styles.emptyState}>
            {state.language === "en"
              ? "No categories"
              : "Категории отсутствуют"}
          </div>
        )}

        <div className={styles.shopsGrid}>
          {filteredShops.map((s) => {
            const title =
              state.language === "en"
                ? s.nameEn && s.nameEn.trim() !== ""
                  ? s.nameEn
                  : s.nameRu
                : s.nameRu;
            return (
              <div
                key={s.id}
                className={styles.shopCard}
                onClick={() => setSelectedShop(s)}
              >
                <div
                  className={styles.shopName}
                  onClick={() => setSelectedShop(s)}
                >
                  {title}
                </div>
              </div>
            );
          })}
          {filteredShops.length === 0 && (
            <div className={styles.emptyState}>
              {state.language === "en"
                ? "No shops found"
                : "Объекты не найдены"}
            </div>
          )}
        </div>
      </section>
    );
  }

  // If on events tab and no shop selected, show events list only when an event is selected
  if (isEventsTab && !shop && events.length > 0 && state.selectedEvent) {
    return (
      <section
        className={styles.container}
        aria-label={state.language === "en" ? "Events list" : "Список событий"}
      >
        <div
          className={styles.shopsGridSimilar}
          ref={eventsGridRef}
          style={{ marginTop: "60px" }}
        >
          {events.map((eventItem) => {
            const eventName =
              state.language === "en"
                ? eventItem.nameEn || eventItem.nameRu
                : eventItem.nameRu;
            return (
              <div
                key={eventItem.id}
                className={styles.eventCard}
                style={{
                  flexShrink: 0,
                  minWidth: "248px",
                  width: "248px",
                  height: "424px",
                }}
                onClick={async () => {
                  // Загружаем полную информацию о событии с фотографиями
                  try {
                    const fullEvent = await apiService.getEventById(
                      eventItem.id,
                    );
                    setSelectedEvent(fullEvent);
                    // Также устанавливаем связанный магазин для подсветки на карте
                    if (fullEvent.shopId) {
                      try {
                        const eventShop = await apiService.getShopById(
                          String(fullEvent.shopId),
                        );
                        setSelectedShop(eventShop);
                      } catch (e) {
                        console.error("Error loading event shop:", e);
                      }
                    }
                  } catch (e) {
                    console.error("Error loading event:", e);
                  }
                }}
              >
                {eventItem.logoUrl ? (
                  <img
                    className={styles.eventShopLogo}
                    src={`${process.env.REACT_APP_MEDIA_PATH}${eventItem.logoUrl}`}
                    alt={eventName}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "data:image/svg+xml,%3Csvg width='248' height='342' viewBox='0 0 248 342' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='248' height='342' fill='%23FFFAF3'/%3E%3Cpath d='M70.4049 112.095C70.0562 111.746 69.6423 111.47 69.1867 111.281C68.7312 111.092 68.2429 110.995 67.7499 110.995C67.2568 110.995 66.7685 111.092 66.313 111.281C65.8574 111.47 65.4435 111.746 65.0949 112.095C64.7462 112.444 64.4696 112.857 64.2809 113.313C64.0922 113.769 63.9951 114.257 63.9951 114.75C63.9951 115.243 64.0922 115.731 64.2809 116.187C64.4696 116.642 64.7462 117.056 65.0949 117.405L75.6624 127.965C72.9462 131.768 71.4904 136.327 71.4999 141V201C71.4999 206.967 73.8704 212.69 78.09 216.91C82.3095 221.129 88.0325 223.5 93.9999 223.5H154C158.673 223.509 163.232 222.054 167.035 219.337L177.595 229.905C178.299 230.609 179.254 231.005 180.25 231.005C181.246 231.005 182.201 230.609 182.905 229.905C183.609 229.201 184.005 228.246 184.005 227.25C184.005 226.254 183.609 225.299 182.905 224.595L70.4049 112.095ZM119.605 171.907C118.306 172.459 117.123 173.251 116.117 174.24L81.0924 208.65C79.7211 206.334 78.9984 203.691 78.9999 201V141C78.9999 138.225 79.7574 135.615 81.0774 133.38L119.605 171.907ZM93.9999 216C91.2249 216 88.6374 215.25 86.4099 213.945L121.375 179.595C122.076 178.908 123.018 178.523 124 178.523C124.981 178.523 125.924 178.908 126.625 179.595L161.59 213.945C159.288 215.293 156.668 216.002 154 216H93.9999ZM169 141V200.092L175.735 206.827C176.245 204.962 176.5 203.02 176.5 201V141C176.5 135.032 174.129 129.31 169.91 125.09C165.69 120.87 159.967 118.5 154 118.5H93.9999C91.9749 118.5 90.0324 118.755 88.1724 119.265L94.8999 126H154C157.978 126 161.793 127.58 164.606 130.393C167.42 133.206 169 137.022 169 141ZM154 152.25C154 150.772 153.709 149.31 153.144 147.945C152.578 146.58 151.749 145.34 150.705 144.295C149.66 143.25 148.42 142.422 147.055 141.856C145.69 141.291 144.227 141 142.75 141C141.272 141 139.81 141.291 138.445 141.856C137.08 142.422 135.84 143.25 134.795 144.295C133.75 145.34 132.922 146.58 132.356 147.945C131.791 149.31 131.5 150.772 131.5 152.25C131.5 155.234 132.685 158.095 134.795 160.205C136.905 162.315 139.766 163.5 142.75 163.5C145.734 163.5 148.595 162.315 150.705 160.205C152.815 158.095 154 155.234 154 152.25ZM146.5 152.25C146.5 153.244 146.105 154.198 145.402 154.902C144.698 155.605 143.744 156 142.75 156C141.755 156 140.801 155.605 140.098 154.902C139.395 154.198 139 153.244 139 152.25C139 151.255 139.395 150.301 140.098 149.598C140.801 148.895 141.755 148.5 142.75 148.5C143.744 148.5 144.698 148.895 145.402 149.598C146.105 150.301 146.5 151.255 146.5 152.25Z' fill='url(%23paint0_linear_5371_938)'/%3E%3Cdefs%3E%3ClinearGradient id='paint0_linear_5371_938' x1='124' y1='110.995' x2='124' y2='231.005' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%2364A696'/%3E%3Cstop offset='0.5' stop-color='%2364A696'/%3E%3Cstop offset='1' stop-color='%2364A696'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E";
                    }}
                  />
                ) : (
                  <img
                    className={styles.eventShopLogo}
                    src="data:image/svg+xml,%3Csvg width='248' height='342' viewBox='0 0 248 342' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='248' height='342' fill='%23FFFAF3'/%3E%3Cpath d='M70.4049 112.095C70.0562 111.746 69.6423 111.47 69.1867 111.281C68.7312 111.092 68.2429 110.995 67.7499 110.995C67.2568 110.995 66.7685 111.092 66.313 111.281C65.8574 111.47 65.4435 111.746 65.0949 112.095C64.7462 112.444 64.4696 112.857 64.2809 113.313C64.0922 113.769 63.9951 114.257 63.9951 114.75C63.9951 115.243 64.0922 115.731 64.2809 116.187C64.4696 116.642 64.7462 117.056 65.0949 117.405L75.6624 127.965C72.9462 131.768 71.4904 136.327 71.4999 141V201C71.4999 206.967 73.8704 212.69 78.09 216.91C82.3095 221.129 88.0325 223.5 93.9999 223.5H154C158.673 223.509 163.232 222.054 167.035 219.337L177.595 229.905C178.299 230.609 179.254 231.005 180.25 231.005C181.246 231.005 182.201 230.609 182.905 229.905C183.609 229.201 184.005 228.246 184.005 227.25C184.005 226.254 183.609 225.299 182.905 224.595L70.4049 112.095ZM119.605 171.907C118.306 172.459 117.123 173.251 116.117 174.24L81.0924 208.65C79.7211 206.334 78.9984 203.691 78.9999 201V141C78.9999 138.225 79.7574 135.615 81.0774 133.38L119.605 171.907ZM93.9999 216C91.2249 216 88.6374 215.25 86.4099 213.945L121.375 179.595C122.076 178.908 123.018 178.523 124 178.523C124.981 178.523 125.924 178.908 126.625 179.595L161.59 213.945C159.288 215.293 156.668 216.002 154 216H93.9999ZM169 141V200.092L175.735 206.827C176.245 204.962 176.5 203.02 176.5 201V141C176.5 135.032 174.129 129.31 169.91 125.09C165.69 120.87 159.967 118.5 154 118.5H93.9999C91.9749 118.5 90.0324 118.755 88.1724 119.265L94.8999 126H154C157.978 126 161.793 127.58 164.606 130.393C167.42 133.206 169 137.022 169 141ZM154 152.25C154 150.772 153.709 149.31 153.144 147.945C152.578 146.58 151.749 145.34 150.705 144.295C149.66 143.25 148.42 142.422 147.055 141.856C145.69 141.291 144.227 141 142.75 141C141.272 141 139.81 141.291 138.445 141.856C137.08 142.422 135.84 143.25 134.795 144.295C133.75 145.34 132.922 146.58 132.356 147.945C131.791 149.31 131.5 150.772 131.5 152.25C131.5 155.234 132.685 158.095 134.795 160.205C136.905 162.315 139.766 163.5 142.75 163.5C145.734 163.5 148.595 162.315 150.705 160.205C152.815 158.095 154 155.234 154 152.25ZM146.5 152.25C146.5 153.244 146.105 154.198 145.402 154.902C144.698 155.605 143.744 156 142.75 156C141.755 156 140.801 155.605 140.098 154.902C139.395 154.198 139 153.244 139 152.25C139 151.255 139.395 150.301 140.098 149.598C140.801 148.895 141.755 148.5 142.75 148.5C143.744 148.5 144.698 148.895 145.402 149.598C146.105 150.301 146.5 151.255 146.5 152.25Z' fill='url(%23paint0_linear_5371_938)'/%3E%3Cdefs%3E%3ClinearGradient id='paint0_linear_5371_938' x1='124' y1='110.995' x2='124' y2='231.005' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%2364A696'/%3E%3Cstop offset='0.5' stop-color='%2364A696'/%3E%3Cstop offset='1' stop-color='%2364A696'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E"
                    alt="No logo"
                  />
                )}
                <div className={styles.eventShopName}>{eventName}</div>
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  // If a shop is selected (for non-editable, non-map tabs), show the existing selected shop panel
  if (shop) {
    const name =
      state.language === "en"
        ? shop.nameEn && shop.nameEn.trim() !== ""
          ? shop.nameEn
          : shop.nameRu
        : shop.nameRu;
    const shortDescription =
      state.language === "en"
        ? shop.shortDescriptionEn && shop.shortDescriptionEn.trim() !== ""
          ? shop.shortDescriptionEn
          : shop.shortDescriptionRu
        : shop.shortDescriptionRu;
    const location =
      state.language === "en"
        ? shop.locationEn && shop.locationEn.trim() !== ""
          ? shop.locationEn
          : shop.locationRu
        : shop.locationRu;
    const workingHours =
      state.language === "en"
        ? shop.workingHoursEn && shop.workingHoursEn.trim() !== ""
          ? shop.workingHoursEn
          : shop.workingHoursRu
        : shop.workingHoursRu;

    // If on events tab, show events grid before shop info only when an event is selected
    if (isEventsTab && events.length > 0 && state.selectedEvent) {
      // Filter out the currently selected event from the list
      const filteredEvents = events.filter(
        (eventItem) => eventItem.id !== state.selectedEvent?.id,
      );

      if (filteredEvents.length === 0) {
        return (
          <section
            className={styles.container}
            aria-label={
              state.language === "en" ? "Events list" : "Список событий"
            }
          >
            <div className={styles.emptyState} style={{ marginTop: "60px" }}>
              {state.language === "en"
                ? "No other events"
                : "Других событий нет"}
            </div>
          </section>
        );
      }

      return (
        <section
          className={styles.container}
          aria-label={
            state.language === "en" ? "Events list" : "Список событий"
          }
        >
          <div
            className={styles.shopsGridSimilar}
            ref={eventsGridRef}
            style={{ marginTop: "60px" }}
          >
            {filteredEvents.map((eventItem) => {
              const eventName =
                state.language === "en"
                  ? eventItem.nameEn || eventItem.nameRu
                  : eventItem.nameRu;
              return (
                <div
                  key={eventItem.id}
                  className={styles.eventCard}
                  style={{
                    flexShrink: 0,
                    minWidth: "248px",
                    width: "248px",
                    height: "424px",
                  }}
                  onClick={async () => {
                    // Загружаем полную информацию о событии с фотографиями
                    try {
                      const fullEvent = await apiService.getEventById(
                        eventItem.id,
                      );
                      setSelectedEvent(fullEvent);
                      // Также устанавливаем связанный магазин для подсветки на карте
                      if (fullEvent.shopId) {
                        try {
                          const eventShop = await apiService.getShopById(
                            String(fullEvent.shopId),
                          );
                          setSelectedShop(eventShop);
                        } catch (e) {
                          console.error("Error loading event shop:", e);
                        }
                      }
                    } catch (e) {
                      console.error("Error loading event:", e);
                    }
                  }}
                >
                  {eventItem.logoUrl ? (
                    <img
                      className={styles.eventShopLogo}
                      src={`${process.env.REACT_APP_MEDIA_PATH}${eventItem.logoUrl}`}
                      alt={eventName}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "data:image/svg+xml,%3Csvg width='248' height='342' viewBox='0 0 248 342' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='248' height='342' fill='%23FFFAF3'/%3E%3Cpath d='M70.4049 112.095C70.0562 111.746 69.6423 111.47 69.1867 111.281C68.7312 111.092 68.2429 110.995 67.7499 110.995C67.2568 110.995 66.7685 111.092 66.313 111.281C65.8574 111.47 65.4435 111.746 65.0949 112.095C64.7462 112.444 64.4696 112.857 64.2809 113.313C64.0922 113.769 63.9951 114.257 63.9951 114.75C63.9951 115.243 64.0922 115.731 64.2809 116.187C64.4696 116.642 64.7462 117.056 65.0949 117.405L75.6624 127.965C72.9462 131.768 71.4904 136.327 71.4999 141V201C71.4999 206.967 73.8704 212.69 78.09 216.91C82.3095 221.129 88.0325 223.5 93.9999 223.5H154C158.673 223.509 163.232 222.054 167.035 219.337L177.595 229.905C178.299 230.609 179.254 231.005 180.25 231.005C181.246 231.005 182.201 230.609 182.905 229.905C183.609 229.201 184.005 228.246 184.005 227.25C184.005 226.254 183.609 225.299 182.905 224.595L70.4049 112.095ZM119.605 171.907C118.306 172.459 117.123 173.251 116.117 174.24L81.0924 208.65C79.7211 206.334 78.9984 203.691 78.9999 201V141C78.9999 138.225 79.7574 135.615 81.0774 133.38L119.605 171.907ZM93.9999 216C91.2249 216 88.6374 215.25 86.4099 213.945L121.375 179.595C122.076 178.908 123.018 178.523 124 178.523C124.981 178.523 125.924 178.908 126.625 179.595L161.59 213.945C159.288 215.293 156.668 216.002 154 216H93.9999ZM169 141V200.092L175.735 206.827C176.245 204.962 176.5 203.02 176.5 201V141C176.5 135.032 174.129 129.31 169.91 125.09C165.69 120.87 159.967 118.5 154 118.5H93.9999C91.9749 118.5 90.0324 118.755 88.1724 119.265L94.8999 126H154C157.978 126 161.793 127.58 164.606 130.393C167.42 133.206 169 137.022 169 141ZM154 152.25C154 150.772 153.709 149.31 153.144 147.945C152.578 146.58 151.749 145.34 150.705 144.295C149.66 143.25 148.42 142.422 147.055 141.856C145.69 141.291 144.227 141 142.75 141C141.272 141 139.81 141.291 138.445 141.856C137.08 142.422 135.84 143.25 134.795 144.295C133.75 145.34 132.922 146.58 132.356 147.945C131.791 149.31 131.5 150.772 131.5 152.25C131.5 155.234 132.685 158.095 134.795 160.205C136.905 162.315 139.766 163.5 142.75 163.5C145.734 163.5 148.595 162.315 150.705 160.205C152.815 158.095 154 155.234 154 152.25ZM146.5 152.25C146.5 153.244 146.105 154.198 145.402 154.902C144.698 155.605 143.744 156 142.75 156C141.755 156 140.801 155.605 140.098 154.902C139.395 154.198 139 153.244 139 152.25C139 151.255 139.395 150.301 140.098 149.598C140.801 148.895 141.755 148.5 142.75 148.5C143.744 148.5 144.698 148.895 145.402 149.598C146.105 150.301 146.5 151.255 146.5 152.25Z' fill='url(%23paint0_linear_5371_938)'/%3E%3Cdefs%3E%3ClinearGradient id='paint0_linear_5371_938' x1='124' y1='110.995' x2='124' y2='231.005' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%2364A696'/%3E%3Cstop offset='0.5' stop-color='%2364A696'/%3E%3Cstop offset='1' stop-color='%2364A696'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E";
                      }}
                    />
                  ) : (
                    <img
                      className={styles.eventShopLogo}
                      src="data:image/svg+xml,%3Csvg width='248' height='342' viewBox='0 0 248 342' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='248' height='342' fill='%23FFFAF3'/%3E%3Cpath d='M70.4049 112.095C70.0562 111.746 69.6423 111.47 69.1867 111.281C68.7312 111.092 68.2429 110.995 67.7499 110.995C67.2568 110.995 66.7685 111.092 66.313 111.281C65.8574 111.47 65.4435 111.746 65.0949 112.095C64.7462 112.444 64.4696 112.857 64.2809 113.313C64.0922 113.769 63.9951 114.257 63.9951 114.75C63.9951 115.243 64.0922 115.731 64.2809 116.187C64.4696 116.642 64.7462 117.056 65.0949 117.405L75.6624 127.965C72.9462 131.768 71.4904 136.327 71.4999 141V201C71.4999 206.967 73.8704 212.69 78.09 216.91C82.3095 221.129 88.0325 223.5 93.9999 223.5H154C158.673 223.509 163.232 222.054 167.035 219.337L177.595 229.905C178.299 230.609 179.254 231.005 180.25 231.005C181.246 231.005 182.201 230.609 182.905 229.905C183.609 229.201 184.005 228.246 184.005 227.25C184.005 226.254 183.609 225.299 182.905 224.595L70.4049 112.095ZM119.605 171.907C118.306 172.459 117.123 173.251 116.117 174.24L81.0924 208.65C79.7211 206.334 78.9984 203.691 78.9999 201V141C78.9999 138.225 79.7574 135.615 81.0774 133.38L119.605 171.907ZM93.9999 216C91.2249 216 88.6374 215.25 86.4099 213.945L121.375 179.595C122.076 178.908 123.018 178.523 124 178.523C124.981 178.523 125.924 178.908 126.625 179.595L161.59 213.945C159.288 215.293 156.668 216.002 154 216H93.9999ZM169 141V200.092L175.735 206.827C176.245 204.962 176.5 203.02 176.5 201V141C176.5 135.032 174.129 129.31 169.91 125.09C165.69 120.87 159.967 118.5 154 118.5H93.9999C91.9749 118.5 90.0324 118.755 88.1724 119.265L94.8999 126H154C157.978 126 161.793 127.58 164.606 130.393C167.42 133.206 169 137.022 169 141ZM154 152.25C154 150.772 153.709 149.31 153.144 147.945C152.578 146.58 151.749 145.34 150.705 144.295C149.66 143.25 148.42 142.422 147.055 141.856C145.69 141.291 144.227 141 142.75 141C141.272 141 139.81 141.291 138.445 141.856C137.08 142.422 135.84 143.25 134.795 144.295C133.75 145.34 132.922 146.58 132.356 147.945C131.791 149.31 131.5 150.772 131.5 152.25C131.5 155.234 132.685 158.095 134.795 160.205C136.905 162.315 139.766 163.5 142.75 163.5C145.734 163.5 148.595 162.315 150.705 160.205C152.815 158.095 154 155.234 154 152.25ZM146.5 152.25C146.5 153.244 146.105 154.198 145.402 154.902C144.698 155.605 143.744 156 142.75 156C141.755 156 140.801 155.605 140.098 154.902C139.395 154.198 139 153.244 139 152.25C139 151.255 139.395 150.301 140.098 149.598C140.801 148.895 141.755 148.5 142.75 148.5C143.744 148.5 144.698 148.895 145.402 149.598C146.105 150.301 146.5 151.255 146.5 152.25Z' fill='url(%23paint0_linear_5371_938)'/%3E%3Cdefs%3E%3ClinearGradient id='paint0_linear_5371_938' x1='124' y1='110.995' x2='124' y2='231.005' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%2364A696'/%3E%3Cstop offset='0.5' stop-color='%2364A696'/%3E%3Cstop offset='1' stop-color='%2364A696'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E"
                      alt="No logo"
                    />
                  )}
                  <div className={styles.eventShopName}>{eventName}</div>
                </div>
              );
            })}
          </div>
        </section>
      );
    }

    return (
      // выбранный объект в событиях

      <section
        className={styles.container}
        aria-label={
          state.language === "en" ? "Selected object" : "Выбранный объект"
        }
      >
        <div>
          <div className={styles.metaRow}>
            <span className={styles.meta}>
              {shop.floor} {state.language === "en" ? "floor" : "этаж"}
            </span>
            {location && <span className={styles.metaLoc}>{location}</span>}
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.cardContainerMap}>
            <div>
              <img
                className={styles.logo}
                src={
                  shop.logoUrl
                    ? `${process.env.REACT_APP_MEDIA_PATH}${shop.logoUrl}`
                    : "data:image/svg+xml,%3Csvg width='348' height='210' viewBox='0 0 348 210' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='348' height='210' fill='white'/%3E%3Cpath d='M120.405 46.0949C120.056 45.7462 119.642 45.4696 119.187 45.2809C118.731 45.0922 118.243 44.9951 117.75 44.9951C117.257 44.9951 116.769 45.0922 116.313 45.2809C115.857 45.4696 115.444 45.7462 115.095 46.0949C114.746 46.4435 114.47 46.8574 114.281 47.313C114.092 47.7685 113.995 48.2568 113.995 48.7499C113.995 49.2429 114.092 49.7312 114.281 50.1867C114.47 50.6423 114.746 51.0562 115.095 51.4049L125.662 61.9649C122.946 65.7678 121.49 70.3265 121.5 74.9999V135C121.5 140.967 123.87 146.69 128.09 150.91C132.31 155.129 138.032 157.5 144 157.5H204C208.673 157.509 213.232 156.054 217.035 153.337L227.595 163.905C228.299 164.609 229.254 165.005 230.25 165.005C231.246 165.005 232.201 164.609 232.905 163.905C233.609 163.201 234.005 162.246 234.005 161.25C234.005 160.254 233.609 159.299 232.905 158.595L120.405 46.0949ZM169.605 105.907C168.306 106.459 167.123 107.251 166.117 108.24L131.092 142.65C129.721 140.334 128.998 137.691 129 135V74.9999C129 72.2249 129.757 69.6149 131.077 67.3799L169.605 105.907ZM144 150C141.225 150 138.637 149.25 136.41 147.945L171.375 113.595C172.076 112.908 173.018 112.523 174 112.523C174.981 112.523 175.924 112.908 176.625 113.595L211.59 147.945C209.288 149.293 206.668 150.002 204 150H144ZM219 74.9999V134.092L225.735 140.827C226.245 138.962 226.5 137.02 226.5 135V74.9999C226.5 69.0325 224.129 63.3095 219.91 59.09C215.69 54.8704 209.967 52.4999 204 52.4999H144C141.975 52.4999 140.032 52.7549 138.172 53.2649L144.9 59.9999H204C207.978 59.9999 211.793 61.5802 214.606 64.3933C217.42 67.2063 219 71.0216 219 74.9999ZM204 86.2499C204 84.7725 203.709 83.3096 203.144 81.9447C202.578 80.5798 201.749 79.3396 200.705 78.2949C199.66 77.2502 198.42 76.4216 197.055 75.8562C195.69 75.2908 194.227 74.9999 192.75 74.9999C191.272 74.9999 189.81 75.2908 188.445 75.8562C187.08 76.4216 185.84 77.2502 184.795 78.2949C183.75 79.3396 182.922 80.5798 182.356 81.9447C181.791 83.3096 181.5 84.7725 181.5 86.2499C181.5 89.2335 182.685 92.095 184.795 94.2048C186.905 96.3146 189.766 97.4999 192.75 97.4999C195.734 97.4999 198.595 96.3146 200.705 94.2048C202.815 92.095 204 89.2335 204 86.2499ZM196.5 86.2499C196.5 87.2444 196.105 88.1982 195.402 88.9015C194.698 89.6048 193.744 89.9999 192.75 89.9999C191.755 89.9999 190.801 89.6048 190.098 88.9015C189.395 88.1982 189 87.2444 189 86.2499C189 85.2553 189.395 84.3015 190.098 83.5982C190.801 82.8949 191.755 82.4999 192.75 82.4999C193.744 82.4999 194.698 82.8949 195.402 83.5982C196.105 84.3015 196.5 85.2553 196.5 86.2499Z' fill='url(%23paint0_linear_5310_528)'/%3E%3Cdefs%3E%3ClinearGradient id='paint0_linear_5310_528' x1='174' y1='44.9951' x2='174' y2='165.005' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%2364A696'/%3E%3Cstop offset='0.5' stop-color='%2364A696'/%3E%3Cstop offset='1' stop-color='%2364A696'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E"
                }
                alt={name}
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "data:image/svg+xml,%3Csvg width='348' height='210' viewBox='0 0 348 210' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='348' height='210' fill='white'/%3E%3Cpath d='M120.405 46.0949C120.056 45.7462 119.642 45.4696 119.187 45.2809C118.731 45.0922 118.243 44.9951 117.75 44.9951C117.257 44.9951 116.769 45.0922 116.313 45.2809C115.857 45.4696 115.444 45.7462 115.095 46.0949C114.746 46.4435 114.47 46.8574 114.281 47.313C114.092 47.7685 113.995 48.2568 113.995 48.7499C113.995 49.2429 114.092 49.7312 114.281 50.1867C114.47 50.6423 114.746 51.0562 115.095 51.4049L125.662 61.9649C122.946 65.7678 121.49 70.3265 121.5 74.9999V135C121.5 140.967 123.87 146.69 128.09 150.91C132.31 155.129 138.032 157.5 144 157.5H204C208.673 157.509 213.232 156.054 217.035 153.337L227.595 163.905C228.299 164.609 229.254 165.005 230.25 165.005C231.246 165.005 232.201 164.609 232.905 163.905C233.609 163.201 234.005 162.246 234.005 161.25C234.005 160.254 233.609 159.299 232.905 158.595L120.405 46.0949ZM169.605 105.907C168.306 106.459 167.123 107.251 166.117 108.24L131.092 142.65C129.721 140.334 128.998 137.691 129 135V74.9999C129 72.2249 129.757 69.6149 131.077 67.3799L169.605 105.907ZM144 150C141.225 150 138.637 149.25 136.41 147.945L171.375 113.595C172.076 112.908 173.018 112.523 174 112.523C174.981 112.523 175.924 112.908 176.625 113.595L211.59 147.945C209.288 149.293 206.668 150.002 204 150H144ZM219 74.9999V134.092L225.735 140.827C226.245 138.962 226.5 137.02 226.5 135V74.9999C226.5 69.0325 224.129 63.3095 219.91 59.09C215.69 54.8704 209.967 52.4999 204 52.4999H144C141.975 52.4999 140.032 52.7549 138.172 53.2649L144.9 59.9999H204C207.978 59.9999 211.793 61.5802 214.606 64.3933C217.42 67.2063 219 71.0216 219 74.9999ZM204 86.2499C204 84.7725 203.709 83.3096 203.144 81.9447C202.578 80.5798 201.749 79.3396 200.705 78.2949C199.66 77.2502 198.42 76.4216 197.055 75.8562C195.69 75.2908 194.227 74.9999 192.75 74.9999C191.272 74.9999 189.81 75.2908 188.445 75.8562C187.08 76.4216 185.84 77.2502 184.795 78.2949C183.75 79.3396 182.922 80.5798 182.356 81.9447C181.791 83.3096 181.5 84.7725 181.5 86.2499C181.5 89.2335 182.685 92.095 184.795 94.2048C186.905 96.3146 189.766 97.4999 192.75 97.4999C195.734 97.4999 198.595 96.3146 200.705 94.2048C202.815 92.095 204 89.2335 204 86.2499ZM196.5 86.2499C196.5 87.2444 196.105 88.1982 195.402 88.9015C194.698 89.6048 193.744 89.9999 192.75 89.9999C191.755 89.9999 190.801 89.6048 190.098 88.9015C189.395 88.1982 189 87.2444 189 86.2499C189 85.2553 189.395 84.3015 190.098 83.5982C190.801 82.8949 191.755 82.4999 192.75 82.4999C193.744 82.4999 194.698 82.8949 195.402 83.5982C196.105 84.3015 196.5 85.2553 196.5 86.2499Z' fill='url(%23paint0_linear_5310_528)'/%3E%3Cdefs%3E%3ClinearGradient id='paint0_linear_5310_528' x1='174' y1='44.9951' x2='174' y2='165.005' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%2364A696'/%3E%3Cstop offset='0.5' stop-color='%2364A696'/%3E%3Cstop offset='1' stop-color='%2364A696'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E";
                }}
              />
            </div>
            <div>
              {workingHours && (
                <div className={styles.hours}>{workingHours}</div>
              )}
            </div>
          </div>
          <div className={styles.nameInfoContainer}>
            <div className={styles.name}>{name}</div>

            {shortDescription && (
              <div className={styles.short}>{shortDescription}</div>
            )}
          </div>
          <div className={styles.actions}>
            <button
              className={`${styles.button} ${styles.primary}`}
              onClick={async () => {
                if (!shop) return;
                const params = new URLSearchParams(routerLocation.search);
                const terminalName = params.get("terminalName");
                try {
                  // Resolve terminal by name
                  const terminals = await apiService.getTerminals({
                    active: true,
                  });
                  const terminal = terminalName
                    ? terminals.find((t) => t.name === terminalName)
                    : undefined;
                  if (!terminal) {
                    setActiveRoutePoints(undefined);
                    return;
                  }
                  // Find route for terminal+shop
                  const routes: RouteEntity[] = await apiService.getRoutes();
                  const match = routes.find(
                    (r) =>
                      String(r.shopId) === String(shop.id) &&
                      String(r.terminalId) === String(terminal.id),
                  );
                  if (match && match.points && match.points.length > 0) {
                    setActiveRoutePoints(match.points);
                    // Save route data for reload
                    setSelectedShopReload(shop);
                    setTerminalName(terminalName || undefined);
                    setCurrentFloorReload(terminal.floor);
                    console.log("Route saved for reload (LowerSection):", {
                      shop: shop.id,
                      terminalName,
                      floor: terminal.floor,
                    });
                    // Open the floor where the terminal is located
                    if (state.currentFloor !== terminal.floor) {
                      setCurrentFloor(terminal.floor);
                    }
                  } else {
                    setActiveRoutePoints(undefined);
                  }
                } catch (e) {
                  setActiveRoutePoints(undefined);
                }
              }}
            >
              {state.language === "en" ? "Build Route" : "ПОСТРОИТЬ МАРШРУТ"}
            </button>
            {/* {state.activeRoutePoints && state.activeRoutePoints.length > 0 && (
              <button
                className={styles.button}
                onClick={() => {
                  const params = new URLSearchParams(routerLocation.search);
                  const terminalName =
                    params.get("terminalName") || state.terminalName || null;
                  setIsQRModalOpen(true);
                }}
              >
                {state.language === "en" ? "Route with you" : "МАРШРУТ С СОБОЙ"}
              </button>
            )} */}
            <button className={styles.button} onClick={handleMoreDetails}>
              {state.language === "en" ? "More details" : "ПОДРОБНЕЕ"}
            </button>
          </div>
        </div>
      </section>
    );
  }

  const params = new URLSearchParams(routerLocation.search);
  const terminalName = params.get("terminalName") || state.terminalName || null;
  const shopId = state.selectedShop?.id;

  return (
    <>
      {null}
      <RouteQRModal
        isOpen={isQRModalOpen}
        onClose={() => setIsQRModalOpen(false)}
        terminalName={terminalName}
        shopId={shopId}
      />
    </>
  );
};

export default LowerSection;
